import React, { useEffect, useState } from 'react';
import { RATING_OPTIONS, isSubmittable, submitFeedback } from '../utils/feedback';
import { trackDoseWiseEvents } from '../utils/analytics';

// DOS-38: in-app feedback widget — quick rating + free text, shown on every
// authenticated page. Context (page, app version) is captured automatically.
const FeedbackWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Detect mobile viewport for adaptive UI (compact + slide-out)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)')
      : undefined as any;
    const apply = () => setIsMobile(mq ? !!mq.matches : false);
    apply();
    if (mq && mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    } else if (mq && (mq as any).addListener) {
      // Safari fallback
      (mq as any).addListener(apply);
      return () => {
        (mq as any).removeListener(apply);
      };
    }
    return;
  }, []);

  const reset = () => {
    setOpen(false);
    setSubmitted(false);
    setRating(null);
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSubmittable(rating, message)) {
      setError('Add a rating or a few words first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitFeedback({
        message,
        rating: rating ?? undefined,
        trigger: 'widget',
      });
      trackDoseWiseEvents.feedbackSubmitted(rating ?? 0, message.trim().length > 0);
      setSubmitted(true);
      setMessage('');
      setRating(null);
    } catch (err) {
      setError('Could not send feedback. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const ratingRow = (
    <div className="flex justify-between gap-1 mb-3" role="radiogroup" aria-label="How is DoseWise working for you?">
      {RATING_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={rating === option.value}
          aria-label={option.label}
          title={option.label}
          className={`flex-1 py-2 rounded-lg text-2xl transition-all duration-150 border ${
            rating === option.value
              ? 'bg-orange-400 border-orange-500 scale-110'
              : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
          }`}
          onClick={() => setRating(rating === option.value ? null : option.value)}
        >
          {option.emoji}
        </button>
      ))}
    </div>
  );

  const form = (
    <form onSubmit={handleSubmit}>
      <p className="m-0 mb-2 text-sm text-gray-300">How is DoseWise working for you?</p>
      {ratingRow}
      <textarea
        className="w-full p-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-50 text-base mb-3 min-h-24 resize-y"
        placeholder="What's working? What's missing? Any bugs?"
        value={message}
        onChange={e => setMessage(e.target.value)}
        maxLength={2000}
      />
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <div className="flex gap-3 mt-2">
        <button type="button" className="flex-1 p-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-50 text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-600" onClick={reset} disabled={loading}>Cancel</button>
        <button type="submit" className="flex-1 p-3 rounded-lg border border-gray-600 bg-orange-400 text-gray-900 text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !isSubmittable(rating, message)}>{loading ? 'Sending...' : 'Send'}</button>
      </div>
    </form>
  );

  const thanks = (
    <>
      <h3 className="m-0 mb-2 text-orange-400">Thank you!</h3>
      <p className="m-0 mb-3">Your feedback has been received — it directly shapes what gets built next.</p>
      <button className="w-full p-3 rounded-lg border border-gray-600 bg-orange-400 text-gray-900 text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-orange-500" onClick={reset}>Close</button>
    </>
  );

  return (
    <>
      <button
        className="fixed bottom-6 right-6 z-2000 bg-orange-400 text-gray-900 border-none rounded-full h-14 px-6 shadow-lg flex items-center justify-center text-xl font-black font-['Space_Grotesk'] cursor-pointer transition-all duration-200 hover:bg-orange-500 hover:shadow-xl gap-3 md:bottom-8 md:right-8 md:min-w-14"
        title="Send feedback"
        onClick={() => setOpen(true)}
      >
        <span role="img" aria-label="Feedback" className="text-2xl">💬</span>
        <span className="font-bold text-lg tracking-wide hidden md:inline">Send Feedback</span>
      </button>
      {open && (
        isMobile ? (
          <div className="fixed right-4 bottom-20 z-2100 w-[92vw] max-w-sm">
            <div className="bg-gray-800 text-gray-50 rounded-xl p-5 shadow-2xl border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="m-0 text-orange-400 text-lg">Send Feedback</h3>
                <button aria-label="Close" className="text-gray-300 hover:text-white" onClick={reset}>✕</button>
              </div>
              {submitted ? thanks : form}
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 w-screen h-screen bg-gray-900 bg-opacity-70 z-2100 flex items-center justify-center" onClick={reset}>
            <div className="bg-gray-800 text-gray-50 rounded-xl p-8 shadow-2xl flex flex-col gap-2 w-11/12 max-w-sm md:max-w-md" onClick={e => e.stopPropagation()}>
              {submitted ? thanks : (
                <>
                  <h3 className="m-0 mb-2 text-orange-400">Send Feedback</h3>
                  {form}
                </>
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};

export default FeedbackWidget;
